import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Parameter, EventSchema, Priority, DataType } from '../../types/schema';
import clsx from 'clsx';
import { ChevronUpIcon, ChevronDownIcon, Trash2, Type, Hash, CheckSquare, SquareStack, Box, CircleDot, Shield, Plus } from 'lucide-react'; // Updated icons
import { getTypeColorClass } from '../../utils/schemaUtils'; // Import utility for type color
import EventUsageBar from './EventUsageBar'; // Import EventUsageBar
import ParameterTreeEditForm from './ParameterTreeEditForm'; // Import ParameterTreeEditForm
import ConfirmDialog from '../dialogs/ConfirmDialog'; // Import ConfirmDialog

interface ParameterTableListProps {
  parameters: Parameter[];
  allEvents: EventSchema[];
  isEditable: boolean;
  handleParameterChange: (paramId: string, updates: Partial<Parameter>) => void;
  handleDeleteParameter: (paramId: string, deleteFromAllEvents?: boolean) => void;
  onAddSubParameter: (parentParam: Parameter) => void; // New prop for adding sub-properties
  paramToEditId: string | null; // This is the initial param to edit from parent
  clearParamToEditId: () => void;
  activeEditingParamId: string | null; // This is the currently active editing param
  setActiveEditingParamId: React.Dispatch<React.SetStateAction<string | null>>;
  handleParameterAssociationChange: (propertyFullPath: string, eventId: string, isAssociated: boolean) => void;
}

type SortKey = 'fullPath' | 'dataTypes' | 'priorities' | 'eventsAssigned';
type SortDirection = 'asc' | 'desc';

const ParameterTableList: React.FC<ParameterTableListProps> = ({
  parameters,
  allEvents,
  isEditable,
  handleParameterChange,
  handleDeleteParameter,
  onAddSubParameter, // Destructure new prop
  paramToEditId,
  clearParamToEditId,
  activeEditingParamId,
  setActiveEditingParamId,
  handleParameterAssociationChange,
}) => {
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection } | null>(null);
  const [hoveredParamId, setHoveredParamId] = useState<string | null>(null); // State to track hovered parameter
  const [unsavedChangesParams, setUnsavedChangesParams] = useState<Set<string>>(new Set()); // State to track unsaved changes
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false); // State for discard confirmation
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogConfirmText, setDialogConfirmText] = useState('Discard');
  const [dialogCancelText, setDialogCancelText] = useState('Keep Editing');
  const [dialogOnConfirm, setDialogOnConfirm] = useState<(() => void) | null>(null);

  // Effect to handle initial editing state if paramToEditId is passed from parent
  useEffect(() => {
    if (paramToEditId && paramToEditId !== activeEditingParamId) {
      setActiveEditingParamId(paramToEditId);
      clearParamToEditId(); // Clear it once set
    }
  }, [paramToEditId, activeEditingParamId, setActiveEditingParamId, clearParamToEditId]);

  const sortedParameters = useMemo(() => {
    let sortableItems = [...parameters];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'fullPath':
            aValue = a.fullPath.toLowerCase();
            bValue = b.fullPath.toLowerCase();
            break;
          case 'dataTypes':
            aValue = a.dataTypes.join(', ').toLowerCase();
            bValue = b.dataTypes.join(', ').toLowerCase();
            break;
          case 'priorities':
            // Custom sort for priorities: Required > Standard > Optional
            const priorityOrder: Record<Priority, number> = { 'Required': 3, 'Standard': 2, 'Optional': 1 };
            aValue = priorityOrder[a.priorities[0] || 'Standard'];
            bValue = priorityOrder[b.priorities[0] || 'Standard'];
            break;
          case 'eventsAssigned':
            aValue = allEvents.filter(event => event.parameterFullPaths.includes(a.fullPath)).length;
            bValue = allEvents.filter(event => event.parameterFullPaths.includes(b.fullPath)).length;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [parameters, sortConfig, allEvents]);

  const requestSort = useCallback((key: SortKey) => {
    let direction: SortDirection = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  const getSortIcon = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUpIcon className="w-4 h-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline-block ml-1" />
    );
  };

  const truncatePath = (path: string, maxLength: number = 40) => {
    if (path.length <= maxLength) {
      return path;
    }
    const parts = path.split('.');
    if (parts.length === 1) {
      return path.substring(0, maxLength - 3) + '...';
    }
    const lastPart = parts[parts.length - 1];
    const firstPart = parts[0];
    const ellipsis = '...';
    const availableLength = maxLength - ellipsis.length - lastPart.length;

    if (availableLength <= firstPart.length + 3) { // If not enough space for first part + ellipsis + last part
      return firstPart.substring(0, availableLength - 3) + ellipsis + lastPart;
    }

    return firstPart + ellipsis + lastPart;
  };

  // Get the appropriate icon for the data type
  const getTypeIcon = useCallback((dataType: DataType) => {
    switch (dataType) {
      case 'string':
        return <Type size={16} />;
      case 'number':
        return <Hash size={16} />;
      case 'boolean':
        return <CheckSquare size={16} />;
      case 'array':
        return <SquareStack size={16} />;
      case 'object':
        return <Box size={16} />;
      case 'null':
        return <CircleDot size={16} />;
      default:
        return <Type size={16} />;
    }
  }, []);

  const getPriorityColorClass = (priority: Priority) => {
    switch (priority) {
      case 'Required': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'Standard': return 'hidden'; // Hide Standard priority
      case 'Optional': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default: return 'hidden';
    }
  };

  const handleExitEditing = useCallback((hasUnsavedChanges: boolean, hasErrors: boolean) => {
    if (hasErrors) {
      // If there are errors, do not exit editing. The form component should handle re-focusing.
      return;
    }

    if (hasUnsavedChanges) {
      setDialogTitle('Discard Changes?');
      setDialogMessage('You have unsaved changes. Do you want to discard them?');
      setDialogConfirmText('Discard');
      setDialogOnConfirm(() => () => {
        // Discard changes by simply exiting editing mode
        setActiveEditingParamId(null);
        setUnsavedChangesParams(prev => {
          const newSet = new Set(prev);
          if (activeEditingParamId) newSet.delete(activeEditingParamId);
          return newSet;
        });
        setShowDiscardConfirmation(false);
      });
      setDialogCancelText('Keep Editing');
      setShowDiscardConfirmation(true);
    } else {
      setActiveEditingParamId(null);
      setUnsavedChangesParams(prev => {
        const newSet = new Set(prev);
        if (activeEditingParamId) newSet.delete(activeEditingParamId);
        return newSet;
      });
    }
  }, [setActiveEditingParamId, setUnsavedChangesParams, activeEditingParamId]);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead>
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[25%] cursor-pointer" // Adjusted width
              onClick={() => requestSort('fullPath')}
            >
              Property Name {getSortIcon('fullPath')}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[30%]" // Adjusted width
            >
              Description
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[15%] cursor-pointer" // Adjusted width
              onClick={() => requestSort('priorities')}
            >
              Priority / PII {getSortIcon('priorities')}
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-[15%] cursor-pointer" // Adjusted width
              onClick={() => requestSort('eventsAssigned')}
            >
              Events {getSortIcon('eventsAssigned')}
            </th>
            <th scope="col" className="relative px-6 py-3 w-[15%]"> {/* Adjusted width */}
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedParameters.map((param) => {
            const isEditingThisParam = activeEditingParamId === param.id;
            const eventsAssignedNames = allEvents
              .filter(event => event.parameterFullPaths.includes(param.fullPath))
              .map(event => event.name);
            const eventsAssignedCount = eventsAssignedNames.length;
            const totalEventsCount = allEvents.length;
            const isHovered = hoveredParamId === param.id;
            const hasUnsavedChanges = unsavedChangesParams.has(param.id);

            return (
              <React.Fragment key={param.id}>
                <tr
                  className={clsx(
                    "hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer",
                    isEditingThisParam ? "bg-blue-50 dark:bg-blue-900/30" : "",
                    hasUnsavedChanges && !isEditingThisParam ? "bg-yellow-50 dark:bg-yellow-900/30" : "" // Indicator for unsaved changes
                  )}
                  onClick={() => !isEditingThisParam && isEditable && setActiveEditingParamId(param.id)}
                  onMouseEnter={() => setHoveredParamId(param.id)}
                  onMouseLeave={() => setHoveredParamId(null)}
                >
                  <td className="px-6 py-2 text-sm font-medium text-gray-900 dark:text-white"> 
                    <div className="flex items-center gap-2">
                      {/* Data Type Icon */}
                      {param.dataTypes && param.dataTypes.length > 0 && (
                        <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(param.dataTypes[0])}`}>
                          {getTypeIcon(param.dataTypes[0])}
                        </span>
                      )}
                      <div>
                        {/* Display full path, with adjusted styling and font */}
                        <div className="font-semibold text-gray-900 dark:text-white" title={param.fullPath}>
                          {truncatePath(param.fullPath, 50)}
                        </div>
                        {/* Removed friendly name */}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300 max-w-xs truncate"> 
                    {param.description}
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300"> 
                    <div className="flex flex-wrap gap-1 items-center">
                      {param.priorities.map((priority) => (
                        <span
                          key={priority}
                          className={clsx(
                            "px-2.5 py-0.5 rounded-full text-xs font-medium",
                            getPriorityColorClass(priority)
                          )}
                        >
                          {priority}
                        </span>
                      ))}
                      {param.isPii && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200">
                          <Shield size={12} className="mr-1" /> PII
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300"> 
                    <EventUsageBar usedCount={eventsAssignedCount} totalCount={totalEventsCount} eventNames={eventsAssignedNames} />
                  </td>
                  <td className="px-6 py-2 text-right text-sm font-medium">
                    <div className={clsx(
                      "flex items-center justify-end space-x-2",
                      isHovered || isEditingThisParam ? 'opacity-100' : 'opacity-0' // Show on hover or when editing
                    )}>
                      {isEditable && (
                        <>
                          {(param.dataTypes.includes('array') || param.dataTypes.includes('object')) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddSubParameter(param); // Call the new simple callback
                              }}
                              className="text-gray-600 hover:text-blue-600 hover:bg-blue-100 dark:text-gray-300 dark:hover:bg-blue-900/50 dark:hover:text-blue-400"
                              title="Add Sub-Property"
                            >
                              <Plus size={16} />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteParameter(param.id);
                            }}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Delete Parameter"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {isEditingThisParam && (
                  <tr>
                    <td colSpan={5} className="p-0"> {/* Span across all columns */}
                      <ParameterTreeEditForm
                        param={param}
                        scopeType={param.scope} // Assuming scope is available on param
                        selectedEvent={null} // Not directly used in table edit form, pass null or current selected event if relevant
                        handleParameterChange={handleParameterChange}
                        isEditable={isEditable}
                        isEditing={true}
                        activeEditingParamId={activeEditingParamId}
                        setActiveEditingParamId={setActiveEditingParamId}
                        allEvents={allEvents}
                        handleParameterAssociationChange={handleParameterAssociationChange}
                        onExitEditing={(hasUnsavedChanges, hasErrors) => {
                          if (hasUnsavedChanges) {
                            setUnsavedChangesParams(prev => new Set(prev).add(param.id));
                          } else {
                            setUnsavedChangesParams(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(param.id);
                              return newSet;
                            });
                          }
                          handleExitEditing(hasUnsavedChanges, hasErrors);
                        }}
                        viewMode="table"
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {parameters.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No properties match your current filters.
        </p>
      )}

      {/* Discard Changes Confirmation Dialog */}
      {showDiscardConfirmation && dialogOnConfirm && (
        <ConfirmDialog
          onClose={() => setShowDiscardConfirmation(false)}
          onConfirm={dialogOnConfirm}
          title={dialogTitle}
          message={dialogMessage}
          confirmText={dialogConfirmText}
          cancelText={dialogCancelText}
          isDanger={false}
        />
      )}
    </div>
  );
};

export default ParameterTableList;
